import React from 'react';
import { useSelector } from 'react-redux';
import { useLocation } from 'react-router-dom';

import { useConfiguration } from '../../context/configurationContext';
import { useRouteConfiguration } from '../../context/routeConfigurationContext';
import { useIntl } from '../../util/reactIntl';
import { createResourceLocatorString } from '../../util/routes';
import { customFormatMoney, convertMoneyToNumber } from '../../util/currency';
import { isScrollingDisabled } from '../../ducks/ui.duck';
import { getListingsById } from '../../ducks/marketplaceData.duck';
import { types as sdkTypes } from '../../util/sdkLoader';
import { listingPageLinkProps } from '../../util/listingSeoUrl';

import { Page, LayoutSingleColumn, NamedLink, H1, H2 } from '../../components';
import TopbarContainer from '../TopbarContainer/TopbarContainer';
import FooterContainer from '../FooterContainer/FooterContainer';
import NotFoundPage from '../NotFoundPage/NotFoundPage';
import { RegNumInput } from '../WP/RegNumInput';
import ListingItem from '../WP/ListingItem/ListingItem';

import Breadcrumbs from './components/Breadcrumbs';
import SeoFaq from './components/SeoFaq';
import SeoCarInfoCard from './components/SeoCarInfoCard';
import {
  pageTypeLabel,
  buildTitle,
  buildDescription,
  buildHeading,
  buildBodyText,
  buildFaqs,
  buildFitTable,
  buildAnswerParagraph,
} from './seoContent';
import {
  breadcrumbListSchema,
  faqPageSchema,
  itemListSchema,
  aggregateOfferSchema,
} from './seoSchema';
import css from './SeoCarPage.module.css';

const { Money } = sdkTypes;

/* Page type is derived from the URL prefix so the component can serve the
 * /dack/* (tire), /falgar/* (rim) and /hjulpaket/* (package answer page)
 * routes. The three differ in their copy, their listing filter and — for the
 * answer page — an extra fit table and a direct-answer paragraph, but the page
 * furniture is identical, so they share this component. */
const pageTypeFromPath = pathname => {
  if (pathname.startsWith('/hjulpaket')) return 'package';
  if (pathname.startsWith('/falgar')) return 'rim';
  return 'tire';
};

/* `customFormatMoney` rather than `formatMoney`: it drops the decimals, which is
 * how every price elsewhere on the marketplace is written. These pages were the
 * only ones saying "14 112,00 kr", and the answer paragraph is meant to be
 * quoted whole — "från 14 112 kr" is what a person would say. */
const formatPrice = (intl, price) => {
  if (price instanceof Money) {
    return customFormatMoney(intl, price);
  }
  return '';
};

export const SeoCarPageComponent = props => {
  const { params } = props;
  const config = useConfiguration();
  const routeConfiguration = useRouteConfiguration();
  const intl = useIntl();
  const location = useLocation();

  const pageType = pageTypeFromPath(location.pathname);
  const labels = pageTypeLabel(pageType);

  const scrollingDisabled = useSelector(isScrollingDisabled);
  const {
    fitment,
    relatedModels,
    listingRefs,
    fetchInProgress,
    liveTotal,
    liveTotalIsAtLeast,
    liveFromPrice,
  } = useSelector(state => state.SeoPages);
  const listings = useSelector(state =>
    getListingsById(state, (listingRefs || []).map(ref => ref.id))
  );

  // No car resolved (and not still loading) → 404.
  if (!fetchInProgress && !fitment) {
    return <NotFoundPage staticContext={props.staticContext} />;
  }

  // While loading on a client-side navigation, render a minimal shell so we
  // don't crash on a null fitment.
  if (!fitment) {
    return (
      <Page title={labels.noun} scrollingDisabled={scrollingDisabled}>
        <LayoutSingleColumn topbar={<TopbarContainer />} footer={<FooterContainer />}>
          <div className={css.root} />
        </LayoutSingleColumn>
      </Page>
    );
  }

  const makeSlug = params.make;
  const modelSlug = params.model;
  const rootUrl = config.marketplaceRootURL || '';

  const title = buildTitle(pageType, fitment, config.marketplaceName);
  const description = buildDescription(pageType, fitment);
  const heading = buildHeading(pageType, fitment);
  const faqs = buildFaqs(pageType, fitment);

  /* P25009-408 — the answer page's two extras: the direct-answer paragraph and
   * the fit fact table. Both are plain render, no fetching, so they are in the
   * HTML the server delivers — which is the whole point, because an AI crawler
   * does not run JavaScript (BR-4).
   *
   * `liveTotalIsAtLeast` is true when the scan hit its ceiling; the paragraph
   * then says "100+" rather than a count it cannot stand behind. */
  const isAnswerPage = pageType === 'package';
  const fromPriceLabel = formatPrice(intl, liveFromPrice);
  const fitTableRows = isAnswerPage ? buildFitTable(fitment) : [];
  const answerParagraph = isAnswerPage
    ? buildAnswerParagraph(fitment, {
        packageCount: liveTotal,
        countIsAtLeast: liveTotalIsAtLeast,
        fromPrice: fromPriceLabel,
      })
    : '';

  const crumbs = [
    { label: 'Hem', path: '/' },
    { label: labels.segmentLabel, path: `/${labels.segment}` },
    { label: fitment.make, path: `/${labels.segment}/${makeSlug}` },
    { label: fitment.model, path: `/${labels.segment}/${makeSlug}/${modelSlug}` },
  ];

  const bodyParagraphs = buildBodyText(pageType, fitment);

  // Build Product/Offer schema items from the live listings. Money → number
  // conversion happens here where intl/currency helpers are available.
  const priceItems = listings.map(listing => {
    const price = listing.attributes?.price;
    let priceValue = null;
    let currency = null;
    if (price instanceof Money) {
      priceValue = convertMoneyToNumber(price);
      currency = price.currency;
    }
    // The canonical listing URL is the breadcrumb one; /l/:id only redirects
    // to it, and structured data should never point at a redirect.
    const { name, params: linkParams } = listingPageLinkProps(listing);
    return {
      id: listing.id.uuid,
      title: listing.attributes?.title,
      priceValue,
      currency,
      url: `${rootUrl}${createResourceLocatorString(name, routeConfiguration, linkParams)}`,
    };
  });

  const aggregateOffer = aggregateOfferSchema(
    priceItems,
    `${labels.noun} till ${fitment.make} ${fitment.model}`
  );

  /* No `brand` here. It used to pass `fitment.make`, which is the car these
   * wheels FIT, not who made them — the same mistake P25009-377 fixed on the
   * listing page, where Google was being told a used wheel set was a "Volvo"
   * product. Brand is a recommended field, and no answer beats a wrong one.
   * P25009-409 BR-4. */
  const schema = [
    breadcrumbListSchema(crumbs, rootUrl),
    faqPageSchema(faqs),
    itemListSchema(priceItems, rootUrl),
    ...(aggregateOffer ? [aggregateOffer] : []),
  ];

  return (
    <Page
      title={title}
      description={description}
      schema={schema}
      scrollingDisabled={scrollingDisabled}
    >
      <LayoutSingleColumn topbar={<TopbarContainer />} footer={<FooterContainer />}>
        <div className={css.root}>
          <Breadcrumbs crumbs={crumbs} />

          <H1 className={css.heading}>{heading}</H1>

          {/* P25009-408 BR-3 / AC-2 — the direct answer, first thing after the
            * heading. This is the sentence an answer engine is meant to lift,
            * so it carries the fit facts AND the live figures together. */}
          {answerParagraph ? <p className={css.answerParagraph}>{answerParagraph}</p> : null}

          {/* The fit facts as a real <table>. A crawler reads a label and its
            * value as a pair only inside a table row; the same facts in a card
            * grid are just words near each other. */}
          {fitTableRows.length > 0 ? (
            <section className={css.fitTableSection}>
              <H2 className={css.sectionHeading}>
                Passform för {fitment.make} {fitment.model}
              </H2>
              <table className={css.fitTable}>
                <caption className={css.fitTableCaption}>
                  Mått som avgör om ett hjul passar en {fitment.make} {fitment.model}. Uppgifterna
                  kommer från vår bildatabas.
                </caption>
                <tbody>
                  {fitTableRows.map(row => (
                    <tr key={row.key}>
                      <th scope="row">{row.label}</th>
                      <td>{row.value}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </section>
          ) : null}

          {/* Top row: clean car-info card (matches the /annonser reg-search box)
              next to the embedded reg search. */}
          <div className={css.topGrid}>
            <SeoCarInfoCard
              fitment={fitment}
              pageType={pageType}
              makeSlug={makeSlug}
              modelSlug={modelSlug}
            />

            <section className={css.searchSection}>
              <H2 className={css.sectionHeading}>Hitta {labels.nounLower} som passar din bil</H2>
              <p className={css.searchHint}>
                Slå ditt registreringsnummer så visar vi alla {labels.nounLower} som passar – du tas
                vidare till annonssidan med rätt filter.
              </p>
              {/* `listingType` preselects the product filter after a plate
                * lookup. The answer page passes 'wheel', which RegNumInput maps
                * to key 0 — complete wheels, i.e. what a package is. */}
              <RegNumInput
                page="searchPage"
                listingType={
                  pageType === 'package' ? 'wheel' : pageType === 'rim' ? 'rim' : 'tire'
                }
                updateState={() => {}}
              />
            </section>
          </div>

          {/* Unique, per-car body copy for SEO. */}
          <section className={css.bodyText}>
            {bodyParagraphs.map((para, i) => (
              <p key={i}>{para}</p>
            ))}
          </section>

          {/* Live listings slice */}
          <section className={css.listingsSection}>
            <div className={css.listingsHeader}>
              <H2 className={css.sectionHeading}>
                {isAnswerPage
                  ? `Hjulpaket till ${fitment.make} ${fitment.model} till salu nu`
                  : `Aktuella annonser för ${fitment.make} ${fitment.model}`}
              </H2>
              <NamedLink
                className={css.seeAllLink}
                name="SearchPageMakeModel"
                params={{ make: makeSlug, model: modelSlug }}
              >
                Se alla annonser →
              </NamedLink>
            </div>

            {listings.length > 0 ? (
              <div className={css.listingsGrid}>
                {listings.map(listing => (
                  <NamedLink
                    key={listing.id.uuid}
                    {...listingPageLinkProps(listing)}
                    className={css.listingLink}
                  >
                    <ListingItem
                      title={listing.attributes.title}
                      location={listing.attributes.publicData?.county || ''}
                      price={formatPrice(intl, listing.attributes.price)}
                      brand={fitment.make}
                    />
                  </NamedLink>
                ))}
              </div>
            ) : (
              <p className={css.emptyState}>
                Inga aktiva annonser just nu.{' '}
                <NamedLink name="SearchPageMakeModel" params={{ make: makeSlug, model: modelSlug }}>
                  Bevaka {fitment.make} {fitment.model} på annonssidan
                </NamedLink>
                .
              </p>
            )}
          </section>

          {/* P25009-408 — the other pages about this same car. A buyer who
            * wants only tyres, or only rims, has a page for that, and a crawler
            * that lands here should be able to reach them. The seller-only
            * "Bygg paket" tool is deliberately not linked: it is behind a
            * login, so a public page must not point at it. */}
          {isAnswerPage ? (
            <section className={css.relatedSection}>
              <H2 className={css.sectionHeading}>
                Mer om {fitment.make} {fitment.model}
              </H2>
              <ul className={css.relatedList}>
                <li>
                  <NamedLink
                    name="SeoTireModelPage"
                    params={{ make: makeSlug, model: modelSlug }}
                  >
                    Däck till {fitment.make} {fitment.model} – storlekar och priser
                  </NamedLink>
                </li>
                <li>
                  <NamedLink name="SeoRimModelPage" params={{ make: makeSlug, model: modelSlug }}>
                    Fälgar till {fitment.make} {fitment.model} – bultmönster och offset
                  </NamedLink>
                </li>
                <li>
                  <NamedLink
                    name="SearchPageMakeModel"
                    params={{ make: makeSlug, model: modelSlug }}
                  >
                    Alla annonser som passar {fitment.make} {fitment.model}
                  </NamedLink>
                </li>
              </ul>
            </section>
          ) : null}

          {/* Internal linking to sibling models */}
          {relatedModels && relatedModels.length > 0 ? (
            <section className={css.relatedSection}>
              <H2 className={css.sectionHeading}>Andra {fitment.make}-modeller</H2>
              <ul className={css.relatedList}>
                {relatedModels.map(rm => (
                  <li key={rm.modelSlug}>
                    <NamedLink
                      name={
                        pageType === 'package'
                          ? 'SeoPackageModelPage'
                          : pageType === 'rim'
                          ? 'SeoRimModelPage'
                          : 'SeoTireModelPage'
                      }
                      params={{ make: rm.brandSlug || makeSlug, model: rm.modelSlug }}
                    >
                      {labels.noun} till {fitment.make} {rm.model}
                    </NamedLink>
                  </li>
                ))}
              </ul>
            </section>
          ) : null}

          <SeoFaq faqs={faqs} />
        </div>
      </LayoutSingleColumn>
    </Page>
  );
};

export default SeoCarPageComponent;
